"use client";

import { FC, useState, useRef, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import EnhanceIcon from "@mui/icons-material/CameraEnhance";

import { useStyles } from "./styles";
import { Image } from "@/types/thread";
import { AlertDialogType } from "@/types/alert";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import { getNewGenerations, addAlertDialog } from "@/redux/actions";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/PrimaryButton";

interface EnhanceImageProps {
  image: Image;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  hideIcon?: boolean;
}

const EnhanceImage: FC<EnhanceImageProps> = ({
  image,
  modalOpen,
  setModalOpen,
  hideIcon = false,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);

  const [isEnhancing, setIsEnhancing] = useState(false);
  const lastEnhanceTimeRef = useRef(0);
  const enhanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpenModal = () => {
    if (!isSubscribed) {
      dispatch(
        addAlertDialog({
          title: "Subscription Required",
          description:
            "Want to enhance your images to be 4K resolution? Upgrade to one of our premium plans to access this feature.",
          nextButtonText: "Upgrade",
          type: AlertDialogType.SubscriptionRequired,
        })
      );
      return;
    }

    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const resetEnhanceState = useCallback(() => {
    setIsEnhancing(false);
    if (enhanceTimeoutRef.current) {
      clearTimeout(enhanceTimeoutRef.current);
      enhanceTimeoutRef.current = null;
    }
  }, []);

  const handleEnhanceImage = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastEnhance = now - lastEnhanceTimeRef.current;

    if (timeSinceLastEnhance < 2000) {
      console.log("Rejecting enhance - too soon after last submission");
      return;
    }

    if (isEnhancing || hasActiveJobs) {
      console.log("Rejecting enhance - already in progress");
      return;
    }

    lastEnhanceTimeRef.current = now;
    setIsEnhancing(true);

    if (enhanceTimeoutRef.current) {
      clearTimeout(enhanceTimeoutRef.current);
    }

    enhanceTimeoutRef.current = setTimeout(() => {
      resetEnhanceState();
    }, 30000);

    try {
      await dispatch(
        getNewGenerations({
          imageEnhance: true,
          imageId: image.id,
        })
      ).unwrap();

      handleModalClose();

      setTimeout(() => {
        resetEnhanceState();
      }, 1000);
    } catch (error) {
      console.error("Error in handleEnhanceImage:", error);
      resetEnhanceState();
    }
  }, [dispatch, image.id, isEnhancing, hasActiveJobs, resetEnhanceState]);

  return (
    <>
      {!hideIcon && (
        <IconButton
          className={classes.icon}
          onClick={handleOpenModal}
          aria-label="Enhance Image"
        >
          <EnhanceIcon />
        </IconButton>
      )}
      <Modal
        heading="Enhance Selected Image"
        open={modalOpen}
        onClose={handleModalClose}
      >
        <div className={classes.previewImage}>
          <img src={image.url} alt={image.alt || "Preview Image"} />
        </div>
        <div className={classes.modalControls}>
          <Button variant="outlined" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={handleEnhanceImage}
            disabled={isEnhancing || hasActiveJobs}
          >
            {isEnhancing ? "Enhancing..." : "Enhance"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default EnhanceImage;
